import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { Lock, Unlock, Trash2, ShieldX, Pencil, X, KeyRound, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
    useEffect(() => { load(); }, []);

    const block = async (id) => { await api.post(`/admin/users/${id}/block`); toast.success("Bloqueado"); load(); };
    const unblock = async (id) => { await api.post(`/admin/users/${id}/unblock`); toast.success("Desbloqueado"); load(); };
    const del = async (id) => {
        if (!window.confirm("Eliminar utilizador? Esta acção é irreversível.")) return;
        await api.delete(`/admin/users/${id}`);
        toast.success("Eliminado"); load();
    };

    return (
        <div data-testid="admin-users-page">
            <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Utilizadores</h1>
            <div className="border-2 border-ink bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink text-white text-xs font-mono uppercase tracking-wider">
                        <tr>
                            <th className="text-left p-3">Nome / Empresa</th>
                            <th className="text-left p-3">Email</th>
                            <th className="text-left p-3">Telefone</th>
                            <th className="text-right p-3">Desconto</th>
                            <th className="text-left p-3">Estado</th>
                            <th className="text-left p-3">Registo</th>
                            <th className="text-right p-3">Acções</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t-2 border-ink/10 hover:bg-magenta/5" data-testid={`user-row-${u.email}`}>
                                <td className="p-3">
                                    <div className="font-bold">{u.name}</div>
                                    {u.company && <div className="text-[11px] font-mono text-ink/60">{u.company}{u.nif ? ` · NIF ${u.nif}` : ""}</div>}
                                </td>
                                <td className="p-3 font-mono text-xs">{u.email}</td>
                                <td className="p-3 font-mono text-xs">{u.phone || "—"}</td>
                                <td className="p-3 text-right font-mono">
                                    {(u.discount_pct ?? 0) > 0 ? <span className="text-magenta font-bold">−{u.discount_pct}%</span> : <span className="text-ink/30">—</span>}
                                    {u.special_prices && Object.keys(u.special_prices).length > 0 && (
                                        <div className="text-[10px] text-ink/50">+{Object.keys(u.special_prices).length} preços especiais</div>
                                    )}
                                </td>
                                <td className="p-3">
                                    {u.is_blocked ? (
                                        <span className="badge-edge bg-destructive text-white border-destructive">
                                            <ShieldX size={12} className="inline mr-1" /> Bloqueado
                                        </span>
                                    ) : (
                                        <span className="badge-edge bg-accent-green text-white border-accent-green">Activo</span>
                                    )}
                                </td>
                                <td className="p-3 font-mono text-xs">{new Date(u.created_at).toLocaleDateString("pt-PT")}</td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(u)} className="text-xs font-bold uppercase hover:text-magenta" data-testid={`edit-user-${u.email}`}>
                                        <Pencil size={12} className="inline" /> Editar
                                    </button>
                                    {u.is_blocked ? (
                                        <button onClick={() => unblock(u.id)} className="ml-3 text-xs font-bold uppercase text-accent-green hover:underline" data-testid={`unblock-${u.email}`}>
                                            <Unlock size={12} className="inline" /> Desbloquear
                                        </button>
                                    ) : (
                                        <button onClick={() => block(u.id)} className="ml-3 text-xs font-bold uppercase text-ink/60 hover:text-magenta" data-testid={`block-${u.email}`}>
                                            <Lock size={12} className="inline" /> Bloquear
                                        </button>
                                    )}
                                    <button onClick={() => del(u.id)} className="ml-3 text-xs font-bold uppercase text-ink/60 hover:text-destructive" data-testid={`delete-user-${u.email}`}>
                                        <Trash2 size={12} className="inline" /> Eliminar
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr><td colSpan={7} className="p-8 text-center font-mono text-ink/50">Sem utilizadores.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && <UserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function UserModal({ user, onClose, onSaved }) {
    const [tab, setTab] = useState("info");
    const [form, setForm] = useState({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        company: user.company || "",
        nif: user.nif || "",
        address: user.address || "",
        notes: user.notes || "",
        discount_pct: user.discount_pct || 0,
        special_prices: user.special_prices || {},
        _pl_brand: "",
        _pl_category: "",
    });
    const [orders, setOrders] = useState(null);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [busy, setBusy] = useState(false);
    const [newPwd, setNewPwd] = useState("");
    const [newSku, setNewSku] = useState("");
    const [newSkuPrice, setNewSkuPrice] = useState("");

    useEffect(() => {
        api.get("/products/brands").then((r) => setBrands(r.data.brands || []));
        api.get("/categories").then((r) => setCategories(r.data));
    }, []);

    useEffect(() => {
        if (tab === "orders" && !orders) {
            api.get(`/admin/users/${user.id}/orders`).then((r) => setOrders(r.data));
        }
    }, [tab, user.id, orders]);

    const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const downloadPricelist = async () => {
        const params = new URLSearchParams();
        if (form._pl_brand) params.set("brand", form._pl_brand);
        if (form._pl_category) params.set("category", form._pl_category);
        try {
            const r = await api.get(`/admin/users/${user.id}/pricelist.csv?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const tag = form._pl_brand || form._pl_category || "todos";
            const safeUser = (user.company || user.name || user.email).replace(/[^a-z0-9]+/gi, "-").toLowerCase();
            a.download = `precos_${safeUser}_${tag}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Lista de preços exportada");
        } catch (e) {
            toast.error("Erro a exportar");
        }
    };

    const save = async () => {
        setBusy(true);
        try {
            const { _pl_brand, _pl_category, ...payload } = form;
            await api.put(`/admin/users/${user.id}`, {
                ...payload,
                discount_pct: Number(form.discount_pct),
            });
            toast.success("Guardado ✓");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally { setBusy(false); }
    };

    const resetPwd = async () => {
        if (newPwd.length < 6) return toast.error("Mínimo 6 caracteres");
        if (!window.confirm(`Definir nova senha para ${user.email}?`)) return;
        setBusy(true);
        try {
            await api.post(`/admin/users/${user.id}/reset-password`, { new_password: newPwd });
            toast.success("Senha redefinida ✓");
            setNewPwd("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally { setBusy(false); }
    };

    const addSpecial = () => {
        if (!newSku || !newSkuPrice) return;
        setForm((s) => ({ ...s, special_prices: { ...s.special_prices, [newSku.trim().toUpperCase()]: Number(newSkuPrice) } }));
        setNewSku(""); setNewSkuPrice("");
    };
    const removeSpecial = (sku) => {
        const sp = { ...form.special_prices };
        delete sp[sku];
        setForm((s) => ({ ...s, special_prices: sp }));
    };

    const tabs = [
        { k: "info", l: "Dados" },
        { k: "discount", l: "Descontos" },
        { k: "specials", l: "Preços especiais" },
        { k: "orders", l: "Encomendas" },
        { k: "password", l: "Senha" },
    ];

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="user-modal">
            <div className="bg-white border-2 border-ink w-full max-w-3xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex justify-between items-center">
                    <div>
                        <h2 className="font-display font-black text-2xl">{user.name}</h2>
                        <div className="text-xs font-mono text-ink/60">{user.email}</div>
                    </div>
                    <button onClick={onClose}><X size={20} /></button>
                </div>

                <div className="border-b-2 border-ink flex flex-wrap">
                    {tabs.map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            className={`px-4 py-3 text-xs font-bold uppercase tracking-wider border-r-2 last:border-r-0 border-ink ${tab === t.k ? "bg-magenta text-white" : "hover:bg-paper"}`}
                            data-testid={`user-tab-${t.k}`}
                        >
                            {t.l}
                        </button>
                    ))}
                </div>

                <div className="p-6 space-y-4 min-h-[300px]">
                    {tab === "info" && (
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Nome" v={form.name} on={upd("name")} />
                            <Field label="Email" v={form.email} on={upd("email")} />
                            <Field label="Telefone" v={form.phone} on={upd("phone")} />
                            <Field label="Empresa" v={form.company} on={upd("company")} />
                            <Field label="NIF" v={form.nif} on={upd("nif")} />
                            <div className="sm:col-span-2">
                                <Field label="Morada" v={form.address} on={upd("address")} />
                            </div>
                            <div className="sm:col-span-2">
                                <span className="label-mono mb-2 block">Notas internas (admin)</span>
                                <textarea value={form.notes} onChange={upd("notes")} rows={3} className="input-edge" />
                            </div>
                        </div>
                    )}

                    {tab === "discount" && (
                        <div className="space-y-4">
                            <Field label="Desconto geral (%) — aplicado em todos os preços" type="number" step="0.01" v={form.discount_pct} on={upd("discount_pct")} />
                            <div className="bg-magenta/5 border-2 border-magenta/40 p-3 text-xs font-mono text-ink/70">
                                Este desconto aplica-se a este cliente automaticamente em todos os produtos da loja.
                                Compõe-se com campanhas activas (escolhe sempre o melhor preço).
                                <br />Exemplo: produto 14,90 € · com desconto {form.discount_pct || 0}% paga {(14.90 * (1 - (form.discount_pct || 0) / 100)).toFixed(2)} €.
                            </div>
                            <div className="border-t-2 border-ink pt-4 space-y-2">
                                <div className="label-mono text-magenta inline-flex items-center gap-1">
                                    Lista de preços para envio comercial
                                </div>
                                <p className="text-xs text-ink/70">
                                    Exporta CSV com SKU + preço base + preço deste cliente (com desconto + preços especiais aplicados).
                                    Útil para enviar ao comercial como proposta B2B.
                                </p>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    <select value={form._pl_brand || ""} onChange={(e) => setForm((s) => ({ ...s, _pl_brand: e.target.value }))} className="input-edge" data-testid="pl-brand">
                                        <option value="">Todas as marcas</option>
                                        {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                    <select value={form._pl_category || ""} onChange={(e) => setForm((s) => ({ ...s, _pl_category: e.target.value }))} className="input-edge" data-testid="pl-category">
                                        <option value="">Todas as categorias</option>
                                        {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                                    </select>
                                </div>
                                <button onClick={downloadPricelist} className="btn-secondary" data-testid="export-pricelist-btn">
                                    📥 Exportar lista de preços CSV
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "specials" && (
                        <div className="space-y-3">
                            <p className="text-sm text-ink/70">Preços fixos por SKU para este cliente. Sobrepõe-se ao preço normal mas o desconto geral aplica-se depois.</p>
                            <div className="border-2 border-ink overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-paper text-xs font-mono uppercase">
                                        <tr><th className="text-left p-2">SKU</th><th className="text-right p-2">Preço s/IVA</th><th className="p-2 w-12"></th></tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(form.special_prices).map(([sku, price]) => (
                                            <tr key={sku} className="border-t border-ink/20" data-testid={`special-${sku}`}>
                                                <td className="p-2 font-mono">{sku}</td>
                                                <td className="p-2 text-right font-mono font-bold">{Number(price).toFixed(2)} €</td>
                                                <td className="p-2 text-right">
                                                    <button onClick={() => removeSpecial(sku)} className="hover:text-destructive"><Trash2 size={12} /></button>
                                                </td>
                                            </tr>
                                        ))}
                                        {Object.keys(form.special_prices).length === 0 && (
                                            <tr><td colSpan={3} className="p-4 text-center text-ink/40 font-mono text-xs">Sem preços especiais</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <input value={newSku} onChange={(e) => setNewSku(e.target.value)} placeholder="SKU (ex: WLHPTNC2612A)" className="input-edge flex-1 min-w-[200px]" data-testid="new-special-sku" />
                                <input value={newSkuPrice} onChange={(e) => setNewSkuPrice(e.target.value)} type="number" step="0.01" placeholder="Preço €" className="input-edge w-32" data-testid="new-special-price" />
                                <button onClick={addSpecial} className="btn-secondary" data-testid="add-special-btn"><Plus size={14} /> Adicionar</button>
                            </div>
                        </div>
                    )}

                    {tab === "orders" && (
                        <div>
                            {orders === null ? (
                                <div className="font-mono text-sm">A carregar…</div>
                            ) : orders.length === 0 ? (
                                <div className="font-mono text-sm text-ink/50">Este cliente ainda não fez encomendas.</div>
                            ) : (
                                <div className="border-2 border-ink overflow-x-auto" data-testid="user-orders-table">
                                    <table className="w-full text-sm">
                                        <thead className="bg-ink text-white text-xs font-mono uppercase">
                                            <tr>
                                                <th className="text-left p-2">Nº</th>
                                                <th className="text-left p-2">Data</th>
                                                <th className="text-left p-2">Estado</th>
                                                <th className="text-right p-2">Itens</th>
                                                <th className="text-right p-2">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.map((o) => (
                                                <tr key={o.id} className="border-t border-ink/20">
                                                    <td className="p-2 font-mono font-bold">#{o.order_number}</td>
                                                    <td className="p-2 font-mono text-xs">{new Date(o.created_at).toLocaleString("pt-PT")}</td>
                                                    <td className="p-2 text-xs">{o.status} · {o.payment_status}</td>
                                                    <td className="p-2 text-right font-mono">{(o.items || []).length}</td>
                                                    <td className="p-2 text-right font-mono font-bold">{fmtEUR(o.total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "password" && (
                        <div className="space-y-4">
                            <div className="bg-magenta/5 border-2 border-magenta/40 p-3 text-xs font-mono text-ink/70 inline-flex items-start gap-2">
                                <KeyRound size={14} className="mt-0.5" />
                                <div>Define uma nova senha para este cliente. Deves comunicá-la ao cliente — ele pode mudar depois na sua área pessoal.</div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <input
                                    type="text"
                                    value={newPwd}
                                    onChange={(e) => setNewPwd(e.target.value)}
                                    placeholder="Nova senha (mín. 6 caracteres)"
                                    className="input-edge flex-1 min-w-[260px] font-mono"
                                    data-testid="new-password-input"
                                />
                                <button onClick={resetPwd} disabled={busy || newPwd.length < 6} className="btn-primary" data-testid="reset-password-btn">
                                    {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Definir senha
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {tab !== "orders" && tab !== "password" && (
                    <div className="p-5 border-t-2 border-ink flex justify-end gap-3">
                        <button onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-user-btn">
                            {busy && <Loader2 size={14} className="animate-spin" />} Guardar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", step }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input type={type} step={step} value={v ?? ""} onChange={on} className="input-edge" />
        </label>
    );
}
